import React, { useState } from 'react';
import { FaChartLine, FaCloudRain, FaTint, FaExclamationTriangle, FaCheckCircle, FaInfoCircle } from 'react-icons/fa';
import './DetailedPredictionAnalysis.css';

const DetailedPredictionAnalysis = ({ village, historyData }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Village-specific scenarios for realistic analysis - Jaipur District, Rajasthan
  const getVillageScenario = (villageName) => {
    const scenarios = {
      // High rainfall, same consumption -> groundwater increase
      'Amber': {
        rainfallTrend: 'high',
        consumptionTrend: 'same',
        description: 'Monsoon recovery scenario',
        rainfallMultiplier: 1.3,
        consumptionMultiplier: 1.0
      },
      'Budhpura_Imd': {
        rainfallTrend: 'high',
        consumptionTrend: 'same',
        description: 'Good monsoon expected',
        rainfallMultiplier: 1.25,
        consumptionMultiplier: 1.0
      },
      'Cgwb Campus Jaipur': {
        rainfallTrend: 'high',
        consumptionTrend: 'same',
        description: 'Favorable rainfall conditions',
        rainfallMultiplier: 1.35,
        consumptionMultiplier: 1.0
      },
      'Durgapura': {
        rainfallTrend: 'high',
        consumptionTrend: 'same',
        description: 'Excellent monsoon forecast',
        rainfallMultiplier: 1.28,
        consumptionMultiplier: 1.0
      },
      'Harmara': {
        rainfallTrend: 'high',
        consumptionTrend: 'same',
        description: 'Above average precipitation',
        rainfallMultiplier: 1.32,
        consumptionMultiplier: 1.0
      },
      
      // Low rainfall, same consumption -> groundwater decrease
      'Heerapura': {
        rainfallTrend: 'low',
        consumptionTrend: 'same',
        description: 'Drought risk scenario',
        rainfallMultiplier: 0.7,
        consumptionMultiplier: 1.0
      },
      'Jaisingpur Khor': {
        rainfallTrend: 'low',
        consumptionTrend: 'same',
        description: 'Water stress conditions',
        rainfallMultiplier: 0.65,
        consumptionMultiplier: 1.0
      },
      'Jhotwara': {
        rainfallTrend: 'low',
        consumptionTrend: 'same',
        description: 'Deficit rainfall expected',
        rainfallMultiplier: 0.68,
        consumptionMultiplier: 1.0
      },
      'Mansarovar': {
        rainfallTrend: 'low',
        consumptionTrend: 'same',
        description: 'Dry spell conditions',
        rainfallMultiplier: 0.72,
        consumptionMultiplier: 1.0
      },
      'Mansarovar Gwd': {
        rainfallTrend: 'low',
        consumptionTrend: 'same',
        description: 'Below normal rainfall',
        rainfallMultiplier: 0.75,
        consumptionMultiplier: 1.0
      },
      'Mes Jaipur': {
        rainfallTrend: 'low',
        consumptionTrend: 'same',
        description: 'Scanty rainfall forecast',
        rainfallMultiplier: 0.63,
        consumptionMultiplier: 1.0
      },
      
      // Normal rainfall, high consumption -> groundwater decrease
      'Bassi Deogaon': {
        rainfallTrend: 'normal',
        consumptionTrend: 'high',
        description: 'Increased water demand',
        rainfallMultiplier: 1.0,
        consumptionMultiplier: 1.4
      },
      'Bassi Gangarampur': {
        rainfallTrend: 'normal',
        consumptionTrend: 'high',
        description: 'Agricultural expansion',
        rainfallMultiplier: 1.0,
        consumptionMultiplier: 1.35
      },
      'Bassi Jatwara': {
        rainfallTrend: 'normal',
        consumptionTrend: 'high',
        description: 'Industrial growth impact',
        rainfallMultiplier: 1.0,
        consumptionMultiplier: 1.45
      },
      'Bassi Madhogarh': {
        rainfallTrend: 'normal',
        consumptionTrend: 'high',
        description: 'Intensive farming practices',
        rainfallMultiplier: 1.0,
        consumptionMultiplier: 1.38
      },
      'Bassi Maharajpura': {
        rainfallTrend: 'normal',
        consumptionTrend: 'high',
        description: 'Textile industry expansion',
        rainfallMultiplier: 1.0,
        consumptionMultiplier: 1.42
      },
      'Bassi Mohanpura': {
        rainfallTrend: 'normal',
        consumptionTrend: 'high',
        description: 'Urban development pressure',
        rainfallMultiplier: 1.0,
        consumptionMultiplier: 1.36
      },
      
      // Normal rainfall, low consumption -> groundwater increase
      'Bassi Padasoli': {
        rainfallTrend: 'normal',
        consumptionTrend: 'low',
        description: 'Water conservation success',
        rainfallMultiplier: 1.0,
        consumptionMultiplier: 0.7
      },
      'Bassi Rojwari': {
        rainfallTrend: 'normal',
        consumptionTrend: 'low',
        description: 'Efficient irrigation adopted',
        rainfallMultiplier: 1.0,
        consumptionMultiplier: 0.75
      },
      'Chaksu Chaksu': {
        rainfallTrend: 'normal',
        consumptionTrend: 'low',
        description: 'Drip irrigation success',
        rainfallMultiplier: 1.0,
        consumptionMultiplier: 0.68
      },
      'Chaksu Goner': {
        rainfallTrend: 'normal',
        consumptionTrend: 'low',
        description: 'Water management excellence',
        rainfallMultiplier: 1.0,
        consumptionMultiplier: 0.72
      },
      'Chaksu Shivdaspura': {
        rainfallTrend: 'normal',
        consumptionTrend: 'low',
        description: 'Conservation technology adoption',
        rainfallMultiplier: 1.0,
        consumptionMultiplier: 0.65
      },
      'Chaksu Thalli': {
        rainfallTrend: 'normal',
        consumptionTrend: 'low',
        description: 'Sustainable farming practices',
        rainfallMultiplier: 1.0,
        consumptionMultiplier: 0.78
      },
      
      // Mixed scenarios - High rainfall, high consumption
      'Chaksu Tootoli': {
        rainfallTrend: 'high',
        consumptionTrend: 'high',
        description: 'Balanced growth scenario',
        rainfallMultiplier: 1.2,
        consumptionMultiplier: 1.2
      },
      'Govindgarh Alisar': {
        rainfallTrend: 'high',
        consumptionTrend: 'high',
        description: 'Rapid development with good rains',
        rainfallMultiplier: 1.25,
        consumptionMultiplier: 1.25
      },
      'Govindgarh Astikalan': {
        rainfallTrend: 'high',
        consumptionTrend: 'high',
        description: 'Industrial boom with monsoon',
        rainfallMultiplier: 1.22,
        consumptionMultiplier: 1.28
      },
      'Govindgarh Dhodsar': {
        rainfallTrend: 'high',
        consumptionTrend: 'high',
        description: 'Agricultural modernization',
        rainfallMultiplier: 1.18,
        consumptionMultiplier: 1.32
      },
      
      // Mixed scenarios - Low rainfall, low consumption
      'Govindgarh Hastera': {
        rainfallTrend: 'low',
        consumptionTrend: 'low',
        description: 'Drought with conservation',
        rainfallMultiplier: 0.8,
        consumptionMultiplier: 0.8
      },
      'Govindgarh Kaladera': {
        rainfallTrend: 'low',
        consumptionTrend: 'low',
        description: 'Adaptive water management',
        rainfallMultiplier: 0.75,
        consumptionMultiplier: 0.75
      },
      'Govindgarh Kanarpura': {
        rainfallTrend: 'low',
        consumptionTrend: 'low',
        description: 'Crisis-driven efficiency',
        rainfallMultiplier: 0.78,
        consumptionMultiplier: 0.72
      },
      'Govindgarh Niwana': {
        rainfallTrend: 'low',
        consumptionTrend: 'low',
        description: 'Emergency conservation mode',
        rainfallMultiplier: 0.82,
        consumptionMultiplier: 0.68
      },
      
      // Additional diverse scenarios
      'Govindgarh Tigaria': {
        rainfallTrend: 'high',
        consumptionTrend: 'low',
        description: 'Optimal water management',
        rainfallMultiplier: 1.3,
        consumptionMultiplier: 0.7
      },
      'Jalsu Rajarampura': {
        rainfallTrend: 'low',
        consumptionTrend: 'high',
        description: 'Critical water stress',
        rainfallMultiplier: 0.7,
        consumptionMultiplier: 1.4
      },
      'Jamwa Ramgarh Datal Gurjran': {
        rainfallTrend: 'normal',
        consumptionTrend: 'same',
        description: 'Stable water conditions',
        rainfallMultiplier: 1.0,
        consumptionMultiplier: 1.0
      },
      'Jamwa Ramgarh Kharana': {
        rainfallTrend: 'high',
        consumptionTrend: 'same',
        description: 'Heritage site advantage',
        rainfallMultiplier: 1.4,
        consumptionMultiplier: 1.0
      },
      'Jamwa Ramgarh Malawala': {
        rainfallTrend: 'normal',
        consumptionTrend: 'high',
        description: 'Tourism pressure',
        rainfallMultiplier: 1.0,
        consumptionMultiplier: 1.5
      },
      'Jamwa Ramgarh Naila': {
        rainfallTrend: 'normal',
        consumptionTrend: 'low',
        description: 'Rural conservation model',
        rainfallMultiplier: 1.0,
        consumptionMultiplier: 0.8
      }
    };
    
    // Default scenario if village not found
    return scenarios[villageName] || {
      rainfallTrend: 'normal',
      consumptionTrend: 'same',
      description: 'Standard conditions',
      rainfallMultiplier: 1.0,
      consumptionMultiplier: 1.0
    };
  };

  // Generate forecast based on village scenario
  const generateScenarioBasedForecast = () => {
    const currentMonth = new Date().getMonth();
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const scenario = getVillageScenario(village.village);
    
    // Base rainfall values (seasonal for Rajasthan)
    const baseRainfall = {
      0: 8, 1: 12, 2: 15, 3: 20, 4: 25, 5: 45, // Jan-Jun
      6: 85, 7: 95, 8: 75, 9: 35, 10: 15, 11: 10 // Jul-Dec
    };
    
    const forecast = [];
    for (let i = 1; i <= 4; i++) {
      const monthIndex = (currentMonth + i) % 12;
      const baseRain = baseRainfall[monthIndex];
      const adjustedRainfall = Math.floor(baseRain * scenario.rainfallMultiplier * (0.8 + Math.random() * 0.4));
      
      forecast.push({
        month: months[monthIndex],
        rainfall: Math.max(2, adjustedRainfall), // Minimum 2mm for Rajasthan
        temperature: Math.floor(Math.random() * 12) + 28, // 28-40°C for Rajasthan
        confidence: Math.floor(Math.random() * 10) + 80, // 80-90%
        scenario: scenario
      });
    }
    return forecast;
  };

  const forecast = generateScenarioBasedForecast();
  const scenario = getVillageScenario(village.village);
  const totalForecastRainfall = forecast.reduce((sum, f) => sum + f.rainfall, 0);
  const avgHistoricalRainfall = historyData?.history
    .filter(h => h.rainfall)
    .reduce((sum, h) => sum + h.rainfall, 0) / 
    (historyData?.history.filter(h => h.rainfall).length || 1);

  // Calculate current extraction rate (mock district-level data)
  const currentExtraction = historyData?.history[historyData.history.length - 1]?.extraction?.ratio || 75;
  const currentWaterDepth = historyData?.history[historyData.history.length - 1]?.waterLevel || village.waterLevel;
  
  // Enhanced impact calculation based on scenario
  const consumptionChange = ((scenario.consumptionMultiplier - 1) * 100).toFixed(0);
  
  // Calculate impact assessment with consumption factor
  const rainfallDifference = totalForecastRainfall - (avgHistoricalRainfall * 0.33);
  const rainfallImpact = rainfallDifference > 20 ? 'positive' : rainfallDifference < -20 ? 'negative' : 'neutral';
  
  // More sophisticated water level prediction for Rajasthan conditions
  const rainfallEffect = ((scenario.rainfallMultiplier - 1) * 100) * 0.02; // Positive for high rainfall, negative for low
  const consumptionEffect = (1 - scenario.consumptionMultiplier) * 1.5; // Positive for low consumption, negative for high
  
  // Only apply extraction effect if there's actual stress (not in stable conditions)
  let extractionEffect = 0;
  if (scenario.rainfallTrend === 'low' || scenario.consumptionTrend === 'high') {
    extractionEffect = currentExtraction > 90 ? -0.6 : currentExtraction > 70 ? -0.3 : 0;
  }
  
  const predictedChange = rainfallEffect + consumptionEffect + extractionEffect;
  const predictedWaterLevel = (parseFloat(currentWaterDepth) + predictedChange).toFixed(2);
  
  // Determine overall scenario outcome
  const getScenarioOutcome = () => {
    const netEffect = rainfallEffect + consumptionEffect;
    
    if (scenario.rainfallTrend === 'high' && scenario.consumptionTrend === 'same') {
      return { type: 'positive', message: 'Groundwater Recovery Expected' };
    } else if (scenario.rainfallTrend === 'high' && scenario.consumptionTrend === 'low') {
      return { type: 'positive', message: 'Optimal Groundwater Recovery' };
    } else if (scenario.rainfallTrend === 'low' && scenario.consumptionTrend === 'same') {
      return { type: 'negative', message: 'Groundwater Depletion Risk' };
    } else if (scenario.rainfallTrend === 'low' && scenario.consumptionTrend === 'high') {
      return { type: 'negative', message: 'Critical Groundwater Stress' };
    } else if (scenario.rainfallTrend === 'normal' && scenario.consumptionTrend === 'high') {
      return { type: 'warning', message: 'Increased Demand Pressure' };
    } else if (scenario.rainfallTrend === 'normal' && scenario.consumptionTrend === 'low') {
      return { type: 'positive', message: 'Conservation Benefits' };
    } else if (scenario.rainfallTrend === 'high' && scenario.consumptionTrend === 'high') {
      return netEffect > 0 ? { type: 'neutral', message: 'Balanced Conditions' } : { type: 'warning', message: 'High Demand Offsetting Rainfall' };
    } else if (scenario.rainfallTrend === 'low' && scenario.consumptionTrend === 'low') {
      return netEffect < -0.5 ? { type: 'warning', message: 'Drought Mitigation Needed' } : { type: 'neutral', message: 'Managed Drought Response' };
    }
    return { type: 'neutral', message: 'Stable Conditions' };
  };
  
  const scenarioOutcome = getScenarioOutcome();
  
  // Enhanced decision recommendations based on Rajasthan scenarios
  const getRecommendations = () => {
    const recommendations = [];
    
    // Scenario-specific recommendations for Rajasthan conditions
    if (scenario.rainfallTrend === 'high' && scenario.consumptionTrend === 'same') {
      recommendations.push({
        type: 'success',
        icon: <FaCheckCircle />,
        title: 'Monsoon Recovery Opportunity',
        description: `Above-average rainfall (${((scenario.rainfallMultiplier - 1) * 100).toFixed(0)}% increase) with stable consumption creates rare groundwater recharge opportunity in Rajasthan.`,
        actions: [
          'Maximize rainwater harvesting during monsoon',
          'Construct check dams and percolation tanks',
          'Plan water storage for dry months ahead'
        ]
      });
    }
    
    if (scenario.rainfallTrend === 'low' && scenario.consumptionTrend === 'same') {
      recommendations.push({
        type: 'critical',
        icon: <FaExclamationTriangle />,
        title: 'Desert State Drought Alert',
        description: `Below-average rainfall (${Math.abs((scenario.rainfallMultiplier - 1) * 100).toFixed(0)}% decrease) in already arid Rajasthan poses severe groundwater crisis.`,
        actions: [
          'Implement emergency water rationing',
          'Deploy water tankers for critical areas',
          'Promote drought-resistant crops like bajra and jowar',
          'Activate traditional water conservation methods'
        ]
      });
    }
    
    if (scenario.rainfallTrend === 'normal' && scenario.consumptionTrend === 'high') {
      recommendations.push({
        type: 'warning',
        icon: <FaExclamationTriangle />,
        title: 'Rajasthan Water Demand Crisis',
        description: `Normal rainfall with increased consumption (${consumptionChange}% rise) strains limited water resources in desert state.`,
        actions: [
          'Implement micro-irrigation systems',
          'Promote water-efficient crops',
          'Regulate groundwater extraction permits',
          'Encourage traditional water harvesting'
        ]
      });
    }
    
    if (scenario.rainfallTrend === 'normal' && scenario.consumptionTrend === 'low') {
      recommendations.push({
        type: 'success',
        icon: <FaCheckCircle />,
        title: 'Desert Conservation Success',
        description: `Normal rainfall with reduced consumption (${Math.abs(consumptionChange)}% decrease) shows excellent water management in arid region.`,
        actions: [
          'Continue traditional water conservation',
          'Share success with neighboring villages',
          'Expand bawri and johad systems',
          'Maintain efficient irrigation practices'
        ]
      });
    }
    
    if (scenario.rainfallTrend === 'high' && scenario.consumptionTrend === 'high') {
      recommendations.push({
        type: 'info',
        icon: <FaInfoCircle />,
        title: 'Monsoon Growth Balance',
        description: `Good rainfall (${((scenario.rainfallMultiplier - 1) * 100).toFixed(0)}% above normal) balances increased demand (${consumptionChange}% rise) in Jaipur district.`,
        actions: [
          'Optimize water use during good monsoon',
          'Build storage for lean periods',
          'Plan sustainable development',
          'Prepare for next dry cycle'
        ]
      });
    }
    
    if (scenario.rainfallTrend === 'low' && scenario.consumptionTrend === 'low') {
      recommendations.push({
        type: 'warning',
        icon: <FaExclamationTriangle />,
        title: 'Rajasthan Drought Management',
        description: `Low rainfall (${Math.abs((scenario.rainfallMultiplier - 1) * 100).toFixed(0)}% below normal) with conservation efforts (${Math.abs(consumptionChange)}% reduction) in desert state.`,
        actions: [
          'Maintain strict water discipline',
          'Enhance traditional conservation',
          'Explore groundwater recharge options',
          'Strengthen community water protocols'
        ]
      });
    }
    
    // Rajasthan-specific extraction recommendations
    if (currentExtraction > 90) {
      recommendations.push({
        type: 'critical',
        icon: <FaExclamationTriangle />,
        title: 'Desert State Over-Extraction Crisis',
        description: `Jaipur district extraction at ${currentExtraction}% is critically dangerous in water-scarce Rajasthan.`,
        actions: [
          'Immediate moratorium on new borewells',
          'Mandatory water meters on all pumps',
          'Promote surface water alternatives',
          'Revive traditional water systems'
        ]
      });
    }
    
    return recommendations;
  };

  const recommendations = getRecommendations();

  if (!isExpanded) {
    return (
      <div className="detailed-prediction-trigger">
        <button 
          className="view-detailed-prediction-btn"
          onClick={() => setIsExpanded(true)}
        >
          <FaChartLine /> View Detailed Prediction Analysis
        </button>
        <p className="trigger-subtitle">
          See 3-4 month forecast with {scenario.description.toLowerCase()} analysis for {village.village}, Jaipur District
        </p>
      </div>
    );
  }

  return (
    <div className="detailed-prediction-analysis">
      <div className="analysis-header">
        <div className="header-content">
          <FaChartLine className="header-icon" />
          <div>
            <h3>Detailed Prediction Analysis - {village.village}, Jaipur</h3>
            <p>3-4 Month Forecast & Impact Assessment ({scenario.description})</p>
          </div>
        </div>
        <button 
          className="collapse-btn"
          onClick={() => setIsExpanded(false)}
        >
          Collapse ▲
        </button>
      </div>

      <div className="analysis-notice">
        <FaInfoCircle />
        <span>Scenario Analysis: {scenario.description} - Rainfall {scenario.rainfallTrend}, Consumption {scenario.consumptionTrend} (Jaipur District, Rajasthan)</span>
      </div>

      {/* Scenario Overview */}
      <div className="scenario-overview">
        <div className={`scenario-card ${scenarioOutcome.type}`}>
          <div className="scenario-header">
            <span className="scenario-icon">
              {scenarioOutcome.type === 'positive' ? '📈' : 
               scenarioOutcome.type === 'negative' ? '📉' : 
               scenarioOutcome.type === 'warning' ? '⚠️' : '📊'}
            </span>
            <h4>{scenarioOutcome.message}</h4>
          </div>
          <div className="scenario-details">
            <div className="scenario-factor">
              <span className="factor-label">Rainfall Trend:</span>
              <span className={`factor-value ${scenario.rainfallTrend}`}>
                {scenario.rainfallTrend === 'high' ? `↑ ${((scenario.rainfallMultiplier - 1) * 100).toFixed(0)}% Above Normal` :
                 scenario.rainfallTrend === 'low' ? `↓ ${Math.abs((scenario.rainfallMultiplier - 1) * 100).toFixed(0)}% Below Normal` :
                 '→ Normal Levels'}
              </span>
            </div>
            <div className="scenario-factor">
              <span className="factor-label">Consumption Pattern:</span>
              <span className={`factor-value ${scenario.consumptionTrend}`}>
                {scenario.consumptionTrend === 'high' ? `↑ ${consumptionChange}% Increase` :
                 scenario.consumptionTrend === 'low' ? `↓ ${Math.abs(consumptionChange)}% Decrease` :
                 '→ Unchanged'}
              </span>
            </div>
            <div className="scenario-factor">
              <span className="factor-label">Groundwater Impact:</span>
              <span className={`factor-value ${predictedChange > 0.2 ? 'increase' : predictedChange < -0.2 ? 'decrease' : 'stable'}`}>
                {predictedChange > 0.2 ? `↗️ +${predictedChange.toFixed(2)}m Increase` :
                 predictedChange < -0.2 ? `↘️ ${predictedChange.toFixed(2)}m Decrease` :
                 '→ Stable Levels'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Current Status Overview */}
      <div className="current-status-grid">
        <div className="status-metric">
          <div className="metric-icon water">
            <FaTint />
          </div>
          <div className="metric-info">
            <span className="metric-label">Current Water Depth</span>
            <span className="metric-value">{currentWaterDepth}m</span>
            <span className="metric-subtitle">As of 2024</span>
          </div>
        </div>

        <div className="status-metric">
          <div className="metric-icon extraction">
            <FaChartLine />
          </div>
          <div className="metric-info">
            <span className="metric-label">District Extraction Rate</span>
            <span className="metric-value" style={{
              color: currentExtraction > 90 ? '#d32f2f' : currentExtraction > 70 ? '#f57c00' : '#2e7d32'
            }}>
              {currentExtraction}%
            </span>
            <span className="metric-subtitle">
              {currentExtraction >= 100 ? 'Over-Exploited' :
               currentExtraction >= 90 ? 'Critical' :
               currentExtraction >= 70 ? 'Semi-Critical' : 'Safe'}
            </span>
          </div>
        </div>

        <div className="status-metric">
          <div className="metric-icon rainfall">
            <FaCloudRain />
          </div>
          <div className="metric-info">
            <span className="metric-label">Avg Historical Rainfall</span>
            <span className="metric-value">{avgHistoricalRainfall.toFixed(0)}mm</span>
            <span className="metric-subtitle">Per 4-month period</span>
          </div>
        </div>
      </div>

      {/* Rainfall Forecast */}
      <div className="forecast-section">
        <h4><FaCloudRain /> Rainfall Forecast (Next 3-4 Months)</h4>
        <div className="forecast-grid">
          {forecast.map((month, index) => (
            <div key={index} className="forecast-card">
              <div className="forecast-month">{month.month}</div>
              <div className="forecast-rainfall">{month.rainfall}mm</div>
              <div className="forecast-temp">{month.temperature}°C</div>
              <div className="forecast-confidence">{month.confidence}% confidence</div>
            </div>
          ))}
        </div>
        {/* <div className="forecast-summary">
          <div className="summary-item">
            <span className="summary-label">Total Expected Rainfall:</span>
            <span className="summary-value">{totalForecastRainfall}mm</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Compared to Historical Avg:</span>
            <span className={`summary-value ${rainfallImpact}`}>
              {rainfallDifference > 0 ? '+' : ''}{rainfallDifference.toFixed(0)}mm 
              ({rainfallDifference > 0 ? '↑' : '↓'} {Math.abs((rainfallDifference / (avgHistoricalRainfall * 0.33)) * 100).toFixed(0)}%)
            </span>
          </div>
        </div> */}
      </div>

      {/* Impact Assessment */}
      <div className="impact-section">
        <h4><FaTint /> Groundwater Impact Assessment</h4>
        <div className="impact-grid">
          <div className={`impact-card ${rainfallImpact}`}>
            <div className="impact-header">
              <span className="impact-icon">
                {rainfallImpact === 'positive' ? '✓' : rainfallImpact === 'negative' ? '⚠' : '→'}
              </span>
              <span className="impact-title">Rainfall Impact</span>
            </div>
            <div className="impact-description">
              {rainfallImpact === 'positive' 
                ? 'Above-average rainfall expected will help recharge groundwater levels'
                : rainfallImpact === 'negative'
                ? 'Below-average rainfall may stress groundwater resources'
                : 'Average rainfall expected, maintain current practices'}
            </div>
          </div>

          <div className={`impact-card ${predictedChange > 0.2 ? 'positive' : predictedChange < -0.2 ? 'negative' : 'neutral'}`}>
            <div className="impact-header">
              <span className="impact-icon">
                {predictedChange > 0.2 ? '📈' : predictedChange < -0.2 ? '📉' : '📊'}
              </span>
              <span className="impact-title">Groundwater Level Change</span>
            </div>
            <div className="impact-description">
              {predictedChange > 0.2 
                ? `Groundwater levels expected to rise by ${predictedChange.toFixed(2)}m due to favorable conditions`
                : predictedChange < -0.2
                ? `Groundwater levels may decline by ${Math.abs(predictedChange).toFixed(2)}m due to stress factors`
                : 'Groundwater levels expected to remain relatively stable'}
            </div>
          </div>

          <div className="impact-card">
            <div className="impact-header">
              <span className="impact-icon">🎯</span>
              <span className="impact-title">Predicted Water Level</span>
            </div>
            <div className="impact-value">
              <span className="predicted-level">{predictedWaterLevel}m</span>
              <span className={`predicted-change ${predictedChange > 0 ? 'positive' : 'negative'}`}>
                ({predictedChange > 0 ? '+' : ''}{predictedChange.toFixed(2)}m change)
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Decision-Making Recommendations */}
      <div className="recommendations-section">
        <h4><FaCheckCircle /> Decision-Making Recommendations</h4>
        {recommendations.map((rec, index) => (
          <div key={index} className={`recommendation-card ${rec.type}`}>
            <div className="recommendation-header">
              <span className="recommendation-icon">{rec.icon}</span>
              <span className="recommendation-title">{rec.title}</span>
            </div>
            <p className="recommendation-description">{rec.description}</p>
            {rec.actions && (
              <ul className="recommendation-actions">
                {rec.actions.map((action, i) => (
                  <li key={i}>{action}</li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>

      {/* Summary */}
      <div className="analysis-summary">
        <h4>Summary</h4>
        <p>
          Based on the forecasted rainfall of <strong>{totalForecastRainfall}mm</strong> over the next 3-4 months 
          and current extraction rate of <strong>{currentExtraction}%</strong>, the groundwater level in {village.village} 
          is expected to {predictedChange > 0 ? 'improve' : 'decline'} by approximately <strong>{Math.abs(predictedChange).toFixed(2)}m</strong>.
          {rainfallImpact === 'positive' && ' The above-average rainfall provides an opportunity for groundwater recovery.'}
          {rainfallImpact === 'negative' && ' Conservation measures are critical due to below-average rainfall forecast.'}
        </p>
      </div>
    </div>
  );
};

export default DetailedPredictionAnalysis;